import createCachedSelector from 're-reselect'
import { createSelector } from 'reselect'
import {
  selectInboxProduct,
  selectKbProduct,
  selectProduct,
} from 'ducks/billing/selectors'
import {
  FEATURE_INBOX_MAX_CHANNELS,
  FEATURE_INBOX_MAX_MAILBOXES,
  FEATURE_INBOX_MAX_REPORTING_DAYS,
  FEATURE_KB_MAX_REPORTING_DAYS,
  billingUsageKeyForProductFeature,
} from 'ducks/billing/featureTypes'
import { INBOX, KB } from 'ducks/billing/productTypes'
import { emptyObj } from 'util/objects'
import { KB_PAGE } from 'ducks/reports/kb'
import { selectCurrentPage } from 'selectors/location'

import {
  selectBillingUsageForProduct,
  selectLocalUsageForProduct,
  selectUsageForProductFeature,
} from './usage'

const byProductCacheKey = (_state, product) => product || 'unknown'
const byProductFeatureCacheKey = (_state, product, feature) =>
  `${product || 'unknown'}-${feature || 'unknown'}`

export const selectProductFeatures = createCachedSelector(
  selectProduct,
  product => product?.features || emptyObj
)(byProductCacheKey)

export const selectInboxFeatures = createSelector(
  selectInboxProduct,
  inboxProduct => inboxProduct.features || emptyObj
)

export const selectKbFeatures = createSelector(
  selectKbProduct,
  kbProduct => kbProduct.features || emptyObj
)

export const selectLimitForProductFeature = createCachedSelector(
  selectProductFeatures,
  (_state, product) => product,
  (_state, _product, feature) => feature,
  (features, product, feature) => {
    // special case:
    // if feature is mailboxes, use channels BUT if limit for channels is unlimited, use value from mailboxes
    if (product === INBOX && feature === FEATURE_INBOX_MAX_MAILBOXES) {
      const channelsFeatureLimit = features[FEATURE_INBOX_MAX_CHANNELS]
      if (channelsFeatureLimit === 'unlimited') return channelsFeatureLimit
    }
    return features[feature]
  }
)(byProductFeatureCacheKey)

export const selectCanUseProductFeature = createCachedSelector(
  selectLimitForProductFeature,
  selectBillingUsageForProduct,
  selectLocalUsageForProduct,
  (_state, product) => product,
  (_state, _product, feature) => feature,
  (featureLimit, billingUsage, localUsage, product, feature) => {
    if ([undefined, null].includes(featureLimit)) {
      return true
    }

    if (featureLimit === 'unlimited') return true
    if (typeof featureLimit === 'boolean') return featureLimit // no need to check usage

    const billingUsageKey = billingUsageKeyForProductFeature(product, feature)
    if (typeof featureLimit === 'number' && billingUsageKey) {
      // check current usage
      const usageValue =
        localUsage[billingUsageKey] || billingUsage[billingUsageKey]

      return Number(usageValue) < featureLimit
    }

    return true
  }
)(byProductFeatureCacheKey)

export const selectProductFeatureUsage = createCachedSelector(
  selectCanUseProductFeature,
  selectLimitForProductFeature,
  selectUsageForProductFeature,
  (canUseFeature, featureLimit, currentUsage) => ({
    canUseFeature,
    featureLimit,
    currentUsage,
  })
)(byProductFeatureCacheKey)

export const selectMaxReportingDaysForProduct = createSelector(
  selectCurrentPage,
  state =>
    selectLimitForProductFeature(
      state,
      INBOX,
      FEATURE_INBOX_MAX_REPORTING_DAYS
    ),
  state =>
    selectLimitForProductFeature(state, KB, FEATURE_KB_MAX_REPORTING_DAYS),
  (currentPage, inboxMaxReportingDays, kbMaxReportingDays) => {
    return currentPage === KB_PAGE ? kbMaxReportingDays : inboxMaxReportingDays
  }
)
